import type {
  DashboardLayout as BaseDashboardLayout,
  DashboardWidgetLayout as BaseDashboardWidgetLayout,
} from "cortex/ui/src/ui/dashboard";

export const DASHBOARD_WIDGET_TYPES = [
  "current-time",
  "device-metrics",
  "device-locations",
  "events-24-hours",
  "last-connected",
  "network-uptime",
  "player-versions",
  "quick-links",
  "recent-incidents",
  "section-heading",
  "strategy-distribution",
  "text-note",
  "total-devices",
  "uptime-average",
] as const;

export type DashboardWidgetType = (typeof DASHBOARD_WIDGET_TYPES)[number];

export const DASHBOARD_WIDGET_TITLES: Record<DashboardWidgetType, string> = {
  "current-time": "Current Time",
  "device-metrics": "Device Metrics",
  "device-locations": "Device Locations",
  "events-24-hours": "Events, Last 24 Hours",
  "last-connected": "Last Connected",
  "network-uptime": "Network Uptime",
  "player-versions": "Player Versions",
  "quick-links": "Quick Links",
  "recent-incidents": "Recent Incidents",
  "section-heading": "Heading",
  "strategy-distribution": "Strategy Distribution",
  "text-note": "Text",
  "total-devices": "Total Devices",
  "uptime-average": "Uptime Average",
};

export const DASHBOARD_WIDGET_DESCRIPTIONS: Record<
  DashboardWidgetType,
  string
> = {
  "current-time": "Show the current date and time in your local timezone.",
  "device-metrics": "Summarize upgrades, pending versions, and new devices.",
  "device-locations": "View the geographic distribution of devices on a map.",
  "events-24-hours": "Show the number of events from the last 24 hours.",
  "last-connected": "Summarize device connectivity and last check-in status.",
  "network-uptime": "Compare uptime trends across networks.",
  "player-versions": "List deployed player versions and device counts.",
  "quick-links": "Add a reusable collection of dashboard shortcuts.",
  "recent-incidents": "Review the latest fleet incidents.",
  "section-heading": "Organize the dashboard with a section title.",
  "strategy-distribution": "Visualize how devices are distributed by strategy.",
  "text-note": "Add explanatory text or operational context.",
  "total-devices": "Show the total number of devices.",
  "uptime-average": "Show yesterday's average production uptime.",
};

export interface DashboardWidgetCategory {
  title: string;
  widgetTypes: ReadonlyArray<DashboardWidgetType>;
}

export const DASHBOARD_WIDGET_CATEGORIES: ReadonlyArray<DashboardWidgetCategory> =
  [
    {
      title: "Summary cards",
      widgetTypes: [
        "last-connected",
        "total-devices",
        "device-metrics",
        "uptime-average",
        "events-24-hours",
      ],
    },
    {
      title: "Charts & maps",
      widgetTypes: [
        "device-locations",
        "network-uptime",
        "strategy-distribution",
      ],
    },
    {
      title: "Tables & lists",
      widgetTypes: ["player-versions", "recent-incidents"],
    },
    {
      title: "Non-data cards",
      widgetTypes: [
        "current-time",
        "quick-links",
        "section-heading",
        "text-note",
      ],
    },
  ];

export const DASHBOARD_WIDGET_DEFAULT_WIDTHS: Record<
  DashboardWidgetType,
  number
> = {
  "current-time": 3,
  "device-metrics": 3,
  "device-locations": 8,
  "events-24-hours": 3,
  "last-connected": 5,
  "network-uptime": 8,
  "player-versions": 4,
  "quick-links": 4,
  "recent-incidents": 6,
  "section-heading": 12,
  "strategy-distribution": 4,
  "text-note": 6,
  "total-devices": 2,
  "uptime-average": 2,
};

export const DASHBOARD_WIDGET_DEFAULT_HEIGHTS: Record<
  DashboardWidgetType,
  number
> = {
  "current-time": 3,
  "device-metrics": 3,
  "device-locations": 12,
  "events-24-hours": 3,
  "last-connected": 3,
  "network-uptime": 6,
  "player-versions": 12,
  "quick-links": 4,
  "recent-incidents": 7,
  "section-heading": 1,
  "strategy-distribution": 7,
  "text-note": 4,
  "total-devices": 3,
  "uptime-average": 3,
};

export const CONFIGURABLE_DASHBOARD_WIDGET_TYPES: ReadonlySet<DashboardWidgetType> =
  new Set(["quick-links", "section-heading", "text-note"]);

export type DashboardWidgetLayout =
  BaseDashboardWidgetLayout<DashboardWidgetType>;
export type DashboardLayout = BaseDashboardLayout<DashboardWidgetType>;

export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  widgets: [
    {
      height: 3,
      id: "last-connected",
      type: "last-connected",
      width: 5,
      x: 0,
      y: 0,
    },
    {
      height: 3,
      id: "total-devices",
      type: "total-devices",
      width: 2,
      x: 5,
      y: 0,
    },
    {
      height: 3,
      id: "device-metrics",
      type: "device-metrics",
      width: 3,
      x: 7,
      y: 0,
    },
    {
      height: 3,
      id: "uptime-average",
      type: "uptime-average",
      width: 2,
      x: 10,
      y: 0,
    },
    {
      id: "device-locations",
      type: "device-locations",
      height: DASHBOARD_WIDGET_DEFAULT_HEIGHTS["device-locations"],
      width: 8,
      x: 0,
      y: 3,
    },
    {
      id: "player-versions",
      type: "player-versions",
      height: DASHBOARD_WIDGET_DEFAULT_HEIGHTS["player-versions"],
      width: 4,
      x: 8,
      y: 3,
    },
  ],
};

export function getDashboardLayoutStorageKey(
  userId: number,
  accountId: number,
) {
  return `fleet-overview-layout:${userId}:${accountId}`;
}

function isDashboardWidgetType(value: unknown): value is DashboardWidgetType {
  return DASHBOARD_WIDGET_TYPES.includes(value as DashboardWidgetType);
}

function isValidWidgetConfig(value: unknown) {
  return (
    value === undefined ||
    (typeof value === "object" && value !== null && !Array.isArray(value))
  );
}

function isValidGridWidth(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 2 &&
    value <= 12
  );
}

function isValidGridPosition(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isValidWidgetHeight(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 14
  );
}

export function isDashboardLayout(value: unknown): value is DashboardLayout {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const layout = value as Partial<DashboardLayout>;
  if (!Array.isArray(layout.widgets)) {
    return false;
  }

  const widgetIds = new Set<string>();

  return layout.widgets.every((widget) => {
    if (typeof widget !== "object" || widget === null) {
      return false;
    }

    const candidate = widget as Partial<DashboardWidgetLayout>;
    if (
      typeof candidate.id !== "string" ||
      candidate.id.length === 0 ||
      widgetIds.has(candidate.id) ||
      !isDashboardWidgetType(candidate.type) ||
      !isValidWidgetConfig(candidate.config) ||
      !isValidGridWidth(candidate.width) ||
      !isValidWidgetHeight(candidate.height) ||
      !isValidGridPosition(candidate.x) ||
      !isValidGridPosition(candidate.y) ||
      candidate.x + candidate.width > 12
    ) {
      return false;
    }

    widgetIds.add(candidate.id);
    return true;
  });
}
